import {
    Collapse,
    Drawer,
    DrawerSize,
    Icon,
    Tag,
    Tooltip
} from "@blueprintjs/core";
import { IconNames, IconSize } from "@blueprintjs/icons";
import classNames from "classnames";
import {
    segmentTrackClickUnavailableCluster,
    segmentTrackSelectClusterSidebar,
    segmentTrackToggleSidebar
} from "in_queue/analytics";
import {
    useActiveClustersWithoutSubscriptions,
    useActiveClustersWithSubscriptions,
    useCluster,
    usePastClustersWithSubscriptions
} from "in_queue/contexts/ClusterContext";
import {
    Cluster,
    getPhaseLabel,
    getStudyCycleLabel,
    getStudyGroupLabel,
    StudyGroup
} from "in_queue/types/clusterType";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import css from "./Sidebar.module.scss";

const ACTIVE_CLUSTERS = "Active Clusters" as const;
const PAST_CLUSTERS = "Past Clusters" as const;
const AVAILABLE_CLUSTERS = "Available Clusters" as const;

type SectionTitle =
    | typeof ACTIVE_CLUSTERS
    | typeof PAST_CLUSTERS
    | typeof AVAILABLE_CLUSTERS;

export const InQueueSidebar = ({
    isSidebarOpen,
    onOpen,
    onClose
}: {
    isSidebarOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
}) => {
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [hideToggleFocus, setHideToggleFocus] = useState(false);

    const activeClusters = useActiveClustersWithSubscriptions();
    const pastClusters = usePastClustersWithSubscriptions();
    const availableClusters = useActiveClustersWithoutSubscriptions();

    const defaultSection =
        activeClusters.length === 0 && pastClusters.length > 0
            ? PAST_CLUSTERS
            : ACTIVE_CLUSTERS;
    const [chosenSection, setChosenSection] = useState<SectionTitle | null>();
    const openSection =
        chosenSection === undefined ? defaultSection : chosenSection;
    const handleToggleSection = (title: SectionTitle) => {
        setChosenSection(openSection === title ? null : title);
    };

    const handleDismiss = () => {
        onClose();
        segmentTrackToggleSidebar({ isOpen: false });
    };

    return (
        <div
            className={css["sidebar-wrapper"]}
            onPointerDownCapture={() => setHideToggleFocus(true)}
            onKeyDownCapture={() => setHideToggleFocus(false)}
        >
            <Drawer
                title="Clusters"
                position="left"
                size={DrawerSize.SMALL}
                isOpen={isSidebarOpen}
                onClose={handleDismiss}
                usePortal={false}
                className={css.sidebar}
                backdropClassName={css["sidebar-backdrop"]}
                shouldReturnFocusOnClose={false}
                onOpening={() => setChosenSection(undefined)}
                onClosed={() => toggleRef.current?.focus()}
            >
                <nav
                    aria-label="Cluster navigation"
                    className={css["sidebar-content"]}
                >
                    <ClustersSection
                        clusters={activeClusters}
                        title={ACTIVE_CLUSTERS}
                        isOpen={openSection === ACTIVE_CLUSTERS}
                        onToggle={handleToggleSection}
                        onSelectCluster={onClose}
                    />
                    {availableClusters.length > 0 && (
                        <ClustersSection
                            clusters={availableClusters}
                            title={AVAILABLE_CLUSTERS}
                            isOpen={openSection === AVAILABLE_CLUSTERS}
                            onToggle={handleToggleSection}
                            onSelectCluster={onClose}
                            disabled
                        />
                    )}
                    <ClustersSection
                        clusters={pastClusters}
                        title={PAST_CLUSTERS}
                        isOpen={openSection === PAST_CLUSTERS}
                        onToggle={handleToggleSection}
                        onSelectCluster={onClose}
                    />
                </nav>
            </Drawer>
            {!isSidebarOpen && (
                <button
                    type="button"
                    ref={toggleRef}
                    aria-label="Show cluster navigation"
                    aria-expanded={false}
                    title="Browse clusters"
                    className={classNames(css["sidebar-toggle"], {
                        [css["sidebar-toggle-pointer-focus"]]: hideToggleFocus
                    })}
                    onBlur={() => setHideToggleFocus(false)}
                    onClick={() => {
                        onOpen();
                        segmentTrackToggleSidebar({
                            isOpen: true,
                            source: "menu"
                        });
                    }}
                >
                    <Icon icon={IconNames.Menu} size={IconSize.LARGE} />
                </button>
            )}
        </div>
    );
};

const ClustersSection = ({
    clusters,
    title,
    isOpen,
    onToggle,
    onSelectCluster,
    disabled = false
}: {
    clusters: Cluster[];
    title: SectionTitle;
    isOpen: boolean;
    onToggle: (title: SectionTitle) => void;
    onSelectCluster: () => void;
    disabled?: boolean;
}) => {
    const clustersByRegion = getRegionToClusterMapping(clusters);

    return (
        <div className={css["clusters-section"]}>
            <button
                type="button"
                onClick={() => onToggle(title)}
                aria-expanded={isOpen}
                className={css["section-header"]}
            >
                {title}
                <Icon
                    icon={isOpen ? IconNames.ChevronUp : IconNames.ChevronDown}
                    size={16}
                    className={css["section-header-icon"]}
                />
            </button>
            <Collapse isOpen={isOpen} keepChildrenMounted>
                <div>
                    {Object.keys(clustersByRegion)
                        .sort()
                        .map((region: string) => (
                            <RegionSection
                                key={region}
                                region={region}
                                clusters={clustersByRegion[region]}
                                disabled={disabled}
                                sectionTitle={title}
                                onSelectCluster={onSelectCluster}
                            />
                        ))}
                </div>
            </Collapse>
        </div>
    );
};

const CONTACT_TOOLTIP = "Contact your Account Manager to get started";

const RegionSection = ({
    region,
    clusters,
    disabled = false,
    sectionTitle,
    onSelectCluster
}: {
    region: string;
    clusters: Cluster[];
    disabled?: boolean;
    sectionTitle: SectionTitle;
    onSelectCluster: () => void;
}) => {
    const currentCluster = useCluster();

    // Group clusters by cycle+phase
    const clustersByCyclePhase = clusters.reduce<Record<string, Cluster[]>>(
        (acc, cluster) => {
            const key = getCyclePhaseLabel(cluster);
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(cluster);
            return acc;
        },
        {}
    );

    const renderClusterLink = (
        cluster: Cluster,
        label: React.ReactNode,
        nested = false
    ) => {
        const isActiveCluster = cluster.slug === currentCluster.slug;
        const linkClass = classNames(css["cluster-link"], {
            [css["nested-cluster-link"]]: nested,
            [css["disabled"]]: disabled,
            [css["is-active"]]: !disabled && isActiveCluster
        });

        if (disabled) {
            return (
                <Tooltip content={CONTACT_TOOLTIP} key={cluster.slug}>
                    <span
                        className={linkClass}
                        onClick={() =>
                            segmentTrackClickUnavailableCluster(cluster)
                        }
                    >
                        {label}
                    </span>
                </Tooltip>
            );
        }

        return (
            <Link
                key={cluster.slug}
                to={cluster.slug}
                className={linkClass}
                onClick={(event) => {
                    segmentTrackSelectClusterSidebar(
                        cluster,
                        sectionTitle === PAST_CLUSTERS ? "past" : "active"
                    );
                    if (
                        event.button === 0 &&
                        !event.metaKey &&
                        !event.ctrlKey &&
                        !event.shiftKey &&
                        !event.altKey
                    ) {
                        onSelectCluster();
                    }
                }}
            >
                {label}{" "}
                {isActiveCluster && (
                    <Tag minimal round intent="primary">
                        Selected
                    </Tag>
                )}
            </Link>
        );
    };

    const sortedKeys = Object.keys(clustersByCyclePhase).sort();
    return (
        <div>
            <div className={css["region-header"]}>{region}</div>
            <div>
                {sortedKeys.map((key) => {
                    const groupClusters = clustersByCyclePhase[key];
                    const hasGroups = groupClusters.some(
                        (c) => c.studyGroup !== StudyGroup.DEFAULT_GROUP
                    );

                    if (!hasGroups) {
                        // Default group clusters are rendered flat
                        return groupClusters.map((cluster) =>
                            renderClusterLink(cluster, key)
                        );
                    }

                    const sortedGroupClusters = [...groupClusters].sort(
                        (a, b) => {
                            const groupA =
                                getStudyGroupLabel(a.region, a.studyGroup) ??
                                "";
                            const groupB =
                                getStudyGroupLabel(b.region, b.studyGroup) ??
                                "";
                            return groupA.localeCompare(groupB);
                        }
                    );

                    // For non-default groups (i.e. MISO), render the cycle+phase as a header
                    // and group clusters by study group
                    return (
                        <div key={key}>
                            <div className={css["cycle-phase-header"]}>
                                {key}
                            </div>
                            {sortedGroupClusters.map((cluster) => {
                                const groupLabel =
                                    getStudyGroupLabel(
                                        cluster.region,
                                        cluster.studyGroup
                                    ) ?? cluster.studyGroup;
                                return renderClusterLink(
                                    cluster,
                                    groupLabel,
                                    true
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const getCyclePhaseLabel = (cluster: Cluster): string => {
    const cycleLabel = getStudyCycleLabel(cluster.region, cluster.studyCycle);
    const phaseLabel = getPhaseLabel(cluster.studyPhase);
    return `${cycleLabel} ${phaseLabel}`;
};

const getRegionToClusterMapping = (
    clusters: Cluster[]
): Record<string, Cluster[]> => {
    const mapping: Record<string, Cluster[]> = {};

    clusters.forEach((cluster: Cluster) => {
        if (cluster.region in mapping) {
            mapping[cluster.region].push(cluster);
        } else {
            mapping[cluster.region] = [cluster];
        }
    });
    return mapping;
};
